/**
 * Enemy schema (DATA_SCHEMAS §4). Enemies define each wave's puzzle. In the
 * Mythic Pantheons theme these are a realm's native creatures plus the shared
 * "Unmade" threat; mechanically they're a stat block + flags + leak/bounty/xp.
 */
import { z } from 'zod';
import { StatBlockSchema } from './common';

export const EnemySchema = z.object({
  id: z.string(),
  name: z.string(),
  stats: StatBlockSchema,
  flags: z
    .object({
      flying: z.boolean().default(false),
      burrows: z.boolean().default(false),
    })
    .default({ flying: false, burrows: false }),
  /** Lives lost if it reaches the Gate. */
  leakCost: z.number().int().positive().default(1),
  /** Gold awarded on death. */
  bounty: z.number().nonnegative().default(0),
  /** Hero XP contribution on death. */
  xp: z.number().nonnegative().default(0),
  /** e.g. ["healAura"] (Shaman), ["castBolt"] (Cultist). */
  abilities: z.array(z.string()).default([]),
  /** Status keywords this enemy ignores. Consumed by the StatusSystem:
   *  `"slowImmune"` makes it immune to slow/freeze (e.g. Talos can't be chilled,
   *  so Frost can't cheese the bronze giant — you must out-damage it). */
  resistKeywords: z.array(z.string()).default([]),
  /** On KILL (not on leak), shatter into adds: spawn `count` of `enemyId` on the
   *  dying enemy's lane, trailing slightly behind it. The iconic finale beat —
   *  Talos bursts into Bronze Shards. Adds with no `onDeath` of their own don't
   *  recurse. */
  onDeath: z
    .object({
      enemyId: z.string(),
      count: z.number().int().positive(),
      /** Path-distance gap between successive shards (spread so they don't stack). */
      spreadDist: z.number().nonnegative().default(36),
    })
    .optional(),
  isBoss: z.boolean().default(false),
});
export type Enemy = z.infer<typeof EnemySchema>;

export function parseEnemy(raw: unknown): Enemy {
  const result = EnemySchema.safeParse(raw);
  if (!result.success) {
    throw new Error(`Invalid enemy data:\n${result.error.toString()}`);
  }
  return result.data;
}
